import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, getDocs, doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

function DeleteRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const fetchDeleteRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const querySnapshot = await getDocs(collection(db, 'deleteRequests'));
      const fetchedRequests = [];

      for (const docObj of querySnapshot.docs) {
        const data = docObj.data();
        let currentLiveStatus = 'active';

        if (data.userId) {
          try {
            const userSnap = await getDoc(doc(db, 'users', data.userId));
            if (!userSnap.exists()) {
              currentLiveStatus = 'purged';
            } else if (userSnap.data().accountStatus === 'suspended') {
              currentLiveStatus = 'suspended';
            }
          } catch (err) {
            console.warn(`Profile lookup fault for UID: ${data.userId}`, err);
          }
        }

        fetchedRequests.push({
          id: docObj.id,
          currentLiveStatus,
          ...data
        });
      }

      fetchedRequests.sort((a, b) => (a.status === 'solved' ? 1 : -1));
      setRequests(fetchedRequests);
    } catch (err) {
      console.error("Data pipeline fetch down:", err);
      setError('Failed to download active deletion records.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeleteRequests();
  }, []);

  // Main Deep Wipe Handler Trigger via Google Cloud Functions
  const handleApproveDeletion = async (request) => {
    if (request.email?.toLowerCase() === 'bikutech44@gmail.com') {
      return alert("Security Warning: App Creator root instance cannot be modified.");
    }

    if (!window.confirm(`⚠️ TOTAL SYSTEM DEEP WIPE WARNING:\n\nThis will trigger an official Google Cloud Function to remove this user from the live Authentication Tab, drop their Firestore profile, delete all listed rooms, and purge their images from Cloudinary.\n\nAre you sure you want to completely erase ${request.email}?`)) return;

    setActionLoadingId(request.id);
    try {
      // 1. Establish secure cloud tunnel wrapper connection to deployed backend functions
      const functions = getFunctions();
      const deepWipeUserAccount = httpsCallable(functions, 'deepWipeUserAccount');

      console.log("Invoking deployed Cloud Function container node...");
      
      // 2. Transmit arguments to execute serverless data purge
      const cloudResult = await deepWipeUserAccount({
        targetUid: request.userId,
        userEmail: request.email
      });

      if (cloudResult.data?.success) {
        // 3. Update the request logging row to solved to preserve entry audit history
        await updateDoc(doc(db, 'deleteRequests', request.id), { status: 'solved' });

        setRequests(prev => prev.map(r => r.id === request.id ? { ...r, status: 'solved', currentLiveStatus: 'purged' } : r));
        alert('Total System Deep Wipe Successful! Account fully expungED from Auth console and data collections dropped.');
      } else {
        throw new Error("Cloud execution engine did not return a valid success token confirmation.");
      }

    } catch (err) {
      console.error("Deep wipe sequence fault processing web response:", err);
      alert(`Wipe Interrupted: ${err.message}`);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRejectRequest = async (requestId) => {
    if (!window.confirm('Reject this request? Profile assets will remain untouched.')) return;
    setActionLoadingId(requestId);
    try {
      await updateDoc(doc(db, 'deleteRequests', requestId), { status: 'solved' });
      setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'solved' } : r));
      alert('Request closed out as resolved.');
    } catch (err) {
      alert('Failed updating log context.');
    } finally {
      setActionLoadingId(null);
    }
  };

  if (loading) return <div style={styles.loading}>Connecting exit telemetry nodes...</div>;
  if (error) return <div style={styles.error}>{error}</div>;

  return (
    <div style={styles.container}>
      <style>{`
        @media (max-width: 900px) {
          .desktop-table-view { display: none !important; }
          .mobile-card-view { display: block !important; }
        }
        @media (min-width: 901px) {
          .mobile-card-view { display: none !important; }
        }
      `}</style>

      <h2 style={styles.title}>Account Exit Actions Center ({requests.length})</h2>

      {requests.length === 0 ? (
        <p style={styles.noData}>Clean database profile. No open account exit requests pending.</p>
      ) : (
        <>
          <table style={styles.table} className="desktop-table-view">
            <thead>
              <tr>
                <th style={styles.th}>Applicant Identity Name</th>
                <th style={styles.th}>Registered Email Node</th>
                <th style={styles.th}>Master Directory Standing</th>
                <th style={styles.th}>Request Standing State</th>
                <th style={styles.th}>Moderator Control Center</th>
              </tr>
            </thead>
            <tbody>
              {requests.map(request => (
                <tr key={request.id} style={styles.tr}>
                  <td style={styles.td}>
                    <strong>{request.firstName} {request.lastName}</strong>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>UID: {request.userId}</div>
                  </td>
                  <td style={styles.td}>{request.email}</td>
                  <td style={styles.td}>
                    <span style={request.currentLiveStatus === 'purged' ? styles.purgedBadge : request.currentLiveStatus === 'suspended' ? styles.suspendedBadge : styles.activeBadge}>
                      {request.currentLiveStatus.toUpperCase()}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={request.status === 'solved' ? styles.solvedBadge : styles.pendingBadge}>
                      {request.status ? request.status.toUpperCase() : 'PENDING'}
                    </span>
                  </td>
                  <td style={styles.tdActions}>
                    {request.status === 'solved' ? (
                      <span style={styles.resolvedText}>✓ Managed by Admins.</span>
                    ) : (
                      <>
                        <button 
                          onClick={() => handleApproveDeletion(request)} 
                          disabled={actionLoadingId !== null} 
                          style={styles.purgeBtn}
                        >
                          {actionLoadingId === request.id ? 'Wiping Everything...' : 'Deep Wipe & Delete'}
                        </button>
                        <button 
                          onClick={() => handleRejectRequest(request.id)} 
                          disabled={actionLoadingId !== null} 
                          style={styles.dismissBtn}
                        >
                          Dismiss
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mobile-card-view">
            {requests.map(request => (
              <div key={request.id} style={styles.card}>
                <div style={styles.cardHeader}>
                  <strong>{request.firstName} {request.lastName}</strong>
                  <span style={request.status === 'solved' ? styles.solvedBadge : styles.pendingBadge}>
                    {request.status ? request.status.toUpperCase() : 'PENDING'}
                  </span>
                </div>
                <div style={styles.cardBody}>
                  <p style={{ margin: '4px 0' }}><strong>Email Node:</strong> {request.email}</p>
                  <p style={{ margin: '4px 0' }}><strong>Account Standing:</strong> {request.currentLiveStatus.toUpperCase()}</p>
                </div>
                <div style={styles.cardActions}>
                  {request.status === 'solved' ? (
                    <div style={styles.mobileResolvedBanner}>Deep Deletion Finalized Across All Systems</div>
                  ) : (
                    <>
                      <button 
                        onClick={() => handleApproveDeletion(request)} 
                        disabled={actionLoadingId !== null} 
                        style={{ ...styles.purgeBtn, flex: 1 }}
                      >
                        {actionLoadingId === request.id ? 'Wiping...' : 'Total System Wipe'}
                      </button>
                      <button 
                        onClick={() => handleRejectRequest(request.id)} 
                        disabled={actionLoadingId !== null} 
                        style={{ ...styles.dismissBtn, flex: 1 }}
                      >
                        Dismiss
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
const styles = {
  /* Layout Containers */
  container: { 
    padding: '20px', 
    backgroundColor: '#fff', 
    borderRadius: '12px', 
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' 
  },

  title: { 
    fontSize: '18px', 
    color: '#1e293b', 
    borderBottom: '1px solid #f1f5f9', 
    paddingBottom: '12px', 
    fontWeight: '600', 
    marginBottom: '20px' 
  },

  /* Table Styles */
  table: { 
    width: '100%', 
    borderCollapse: 'collapse' 
  },

  th: { 
    textAlign: 'left', 
    padding: '12px', 
    color: '#64748b', 
    fontSize: '13px', 
    borderBottom: '2px solid #f1f5f9', 
    fontWeight: '600' 
  },

  td: { 
    padding: '14px 12px', 
    borderBottom: '1px solid #f1f5f9', 
    fontSize: '13px', 
    color: '#334155', 
    verticalAlign: 'middle' 
  },

  tdActions: { 
    padding: '12px', 
    borderBottom: '1px solid #f1f5f9', 
    display: 'flex', 
    gap: '6px', 
    alignItems: 'center' 
  },

  /* Banners & Text States */
  resolvedText: { 
    color: '#16a34a', 
    fontWeight: '600', 
    fontSize: '13px', 
    paddingLeft: '4px', 
    paddingBottom: '12px',
    paddingTop: '6px'
  },

  mobileResolvedBanner: { 
    width: '100%', 
    textAlign: 'center', 
    backgroundColor: '#f0fdf4', 
    color: '#166534', 
    padding: '10px', 
    borderRadius: '8px', 
    fontWeight: '600', 
    fontSize: '12px', 
    borderWidth: '1px', 
    borderColor: '#bbf7d0' 
  },

  /* Status Badges */
  activeBadge: { 
    backgroundColor: '#e0f2fe', 
    color: '#0369a1', 
    padding: '3px 8px', 
    borderRadius: '20px', 
    fontSize: '11px', 
    fontWeight: 'bold' 
  },

  suspendedBadge: { 
    backgroundColor: '#fee2e2', 
    color: '#991b1b', 
    padding: '3px 8px', 
    borderRadius: '20px', 
    fontSize: '11px', 
    fontWeight: 'bold' 
  },

  purgedBadge: { 
    backgroundColor: '#f1f5f9', 
    color: '#64748b', 
    padding: '3px 8px', 
    borderRadius: '20px', 
    fontSize: '11px', 
    fontWeight: 'bold' 
  },

  pendingBadge: { 
    backgroundColor: '#fef3c7', 
    color: '#d97706', 
    padding: '3px 8px', 
    borderRadius: '20px', 
    fontSize: '11px', 
    fontWeight: 'bold' 
  },

  solvedBadge: { 
    backgroundColor: '#e2e8f0', 
    color: '#475569', 
    padding: '3px 8px', 
    borderRadius: '20px', 
    fontSize: '11px', 
    fontWeight: 'bold' 
  },

  /* Buttons */
  purgeBtn: { 
    padding: '6px 12px', 
    backgroundColor: '#ef4444', 
    color: 'white', 
    border: 'none', 
    borderRadius: '6px', 
    cursor: 'pointer', 
    fontSize: '12px', 
    fontWeight: '500' 
  },

  dismissBtn: { 
    padding: '6px 12px', 
    backgroundColor: '#64748b', 
    color: 'white', 
    border: 'none', 
    borderRadius: '6px', 
    cursor: 'pointer', 
    fontSize: '12px', 
    fontWeight: '500' 
  },

  /* Mobile Components */
  card: { 
    border: '1px solid #e2e8f0', 
    borderRadius: '12px', 
    padding: '16px', 
    marginBottom: '16px', 
    backgroundColor: '#f8fafc' 
  },

  cardHeader: { 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center', 
    borderBottom: '1px solid #e2e8f0', 
    paddingBottom: '8px' 
  },

  cardBody: { 
    padding: '10px 0', 
    color: '#475569', 
    fontSize: '14px' 
  },

  cardActions: { 
    display: 'flex', 
    gap: '8px', 
    marginTop: '6px' 
  },

  /* Feedback States */
  loading: { 
    textAlign: 'center', 
    padding: '60px', 
    color: '#64748b' 
  },

  error: { 
    color: '#ef4444', 
    textAlign: 'center', 
    padding: '20px' 
  },

  noData: { 
    textAlign: 'center', 
    padding: '30px', 
    color: '#94a3b8' 
  }
};
export default DeleteRequests;